import { http } from "./http";
import type { Presence } from "./presence.api";
import type { WorkspaceReference, ResolvedReference } from "./references.api";
export type MessageBody = {
  bodyVersion: 1;
  segments: (
    | { kind: "text"; text: string }
    | { kind: "reference"; reference: WorkspaceReference }
  )[];
};
export type Conversation = {
  id: string;
  title: string;
  type: "DIRECT" | "GROUP" | "SCHOOL" | "AGENCY";
  schoolId: string | null;
  unread: number;
  updatedAt: string;
  canManage: boolean;
  /** Échange direct : l'interlocuteur et sa présence ; `null` pour un groupe ou un salon. */
  peerId?: string | null;
  presence?: Presence | null;
};
export type MessageFile = {
  id: string;
  name: string;
  size: number;
  mime?: string;
};
export type MessageSearch = {
  q?: string;
  authorId?: string;
  entityType?: string;
  entityId?: string;
};
export type Message = {
  files?: MessageFile[];
  id: string;
  sequence: number;
  authorId: string;
  authorName: string;
  content: MessageBody | null;
  references: (ResolvedReference & { schoolId?: string })[];
  reactions: { emoji: string; count: number; mine: boolean }[];
  tasks: { id: string; schoolId: string }[];
  version: number;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
};
export type Directory = {
  items: {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
    schoolId: string | null;
    presence?: Presence | null;
  }[];
  hasMore: boolean;
  schools: { id: string; name: string }[];
  agencyId: string | null;
  role: string;
};
const root = "/v1/messaging";
export const messagingApi = {
  directory: async () => (await http.get<Directory>(root + "/directory")).data,
  list: async () =>
    (
      await http.get<{
        items: Conversation[];
        unread: number;
        unreadConversations: number;
      }>(root + "/conversations")
    ).data,
  create: async (payload: {
    requestId: string;
    type: Conversation["type"];
    title: string;
    participants: string[];
    schoolId?: string;
  }) =>
    (await http.post<{ id: string }>(root + "/conversations", payload)).data,
  messages: async (id: string, before?: number, search?: MessageSearch) =>
    (
      await http.get<{ items: Message[]; hasMore: boolean }>(
        `${root}/conversations/${id}/messages`,
        { params: { before, ...search } },
      )
    ).data,
  send: async (
    id: string,
    requestId: string,
    content: MessageBody,
    fileIds: string[] = [],
  ) =>
    (
      await http.post(`${root}/conversations/${id}/messages`, {
        requestId,
        content,
        fileIds,
      })
    ).data,
  edit: async (
    id: string,
    m: Message,
    requestId: string,
    content: MessageBody,
  ) =>
    (
      await http.patch(`${root}/conversations/${id}/messages/${m.id}`, {
        requestId,
        content,
        version: m.version,
      })
    ).data,
  remove: async (id: string, m: Message, requestId: string) =>
    (
      await http.post(`${root}/conversations/${id}/messages/${m.id}/delete`, {
        requestId,
        version: m.version,
      })
    ).data,
  react: async (
    id: string,
    m: string,
    emoji: string,
    active: boolean,
    requestId: string,
  ) =>
    (
      await http.post(`${root}/conversations/${id}/messages/${m}/reactions`, {
        requestId,
        emoji,
        active,
      })
    ).data,
  read: async (id: string, sequence: number) =>
    (await http.post(`${root}/conversations/${id}/read`, { sequence })).data,
  members: async (id: string) =>
    (
      await http.get<{ id: string; name: string; leftAt: string | null }[]>(
        `${root}/conversations/${id}/members`,
      )
    ).data,
  setMembers: async (id: string, participants: string[], requestId: string) =>
    (
      await http.post(`${root}/conversations/${id}/members`, {
        requestId,
        participants,
      })
    ).data,
  task: async (
    id: string,
    message: string,
    payload: {
      requestId: string;
      schoolId: string;
      title: string;
      description: string;
      priority: string;
      dueDate?: string;
      assigneeId?: string;
      reference?: WorkspaceReference;
    },
  ) =>
    (
      await http.post<{ id: string; schoolId: string }>(
        `${root}/conversations/${id}/messages/${message}/task`,
        payload,
      )
    ).data,
  source: async (task: string) =>
    (
      await http.get<{
        items: {
          conversationId: string;
          messageId: string;
          sequence: number;
        }[];
      }>(`${root}/tasks/${task}/source`)
    ).data,
};
export function shareInMessaging(reference: WorkspaceReference) {
  window.dispatchEvent(
    new CustomEvent("permio:share-message", { detail: { reference } }),
  );
}
export function openMessaging(conversationId?: string, sequence?: number) {
  window.dispatchEvent(
    new CustomEvent("permio:share-message", {
      detail: { conversationId, sequence },
    }),
  );
}
